import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Phone, MapPin, Clock, Video, Home, Quote } from "lucide-react";
import { API_URL } from "../lib/brand";

const CALENDLY_URL = "https://calendly.com/teresavilardoconsulente/call_conoscitiva";

const trackClick = (cta, service_key) => {
  axios.post(`${API_URL}/analytics/cta-click`, { cta, service_key }).catch(() => {});
};

const CONSULENZE = [
  { key: "conoscitiva", label: "Conoscitiva", mode: "Online", duration: "30 min", note: "gratuita", desc: "Il nostro primo incontro per conoscerci e capire come posso esserti utile.", accent: "#81C3C9", icon: Video },
  { key: "flash",       label: "Flash",       mode: "Online", duration: "1 ora",   note: "singola",  desc: "Una domanda urgente, una risposta di sguardo esperto. Perfetta per fare chiarezza in fretta.", accent: "#FFE45C", icon: Video },
  { key: "starter",     label: "Starter",     mode: "Online", duration: "1h30 + follow-up 20 min", note: null, desc: "Per iniziare un cammino: osservazione della relazione e del bambino via videochiamata, con follow-up per assestare i primi cambiamenti.", accent: "#4E9EA5", icon: Video },
  { key: "pro",         label: "Pro",         mode: "Online", duration: "3 incontri × 1 ora", note: null, desc: "Un percorso strutturato per attraversare un cambiamento importante con calma, tutto in videochiamata.", accent: "#FF2768", icon: Video },
  { key: "elite",       label: "Elite",       mode: "Online", duration: "3 incontri × 1h30", note: null, desc: "Il percorso più profondo, in videochiamata: ascolto, osservazione e presenza vera, con più spazio per approfondire ogni dettaglio.", accent: "#3D2140", icon: Video },
];

const TATA_DOMICILIO = [
  { key: "diurno",   label: "Accudimento Diurno",   desc: "Assistenza e gioco durante il giorno, secondo i ritmi del bambino.", accent: "#FFE45C" },
  { key: "notturno", label: "Accudimento Notturno", desc: "Sonni tranquilli per tutta la famiglia — presente, ma discreta.", accent: "#4E9EA5" },
  { key: "vacanza",  label: "Tata in Vacanza",      desc: "Supporto completo in viaggio, così i genitori possono davvero riposare.", accent: "#FF2768" },
];

export function ComeTiPossoAiutare() {
  const [modal, setModal] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const consRef = useRef(null);
  const tataRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/testimonials`).then(r => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  const scroll = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  return (
    <>
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#FDECEF]/40" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-bold">— Come ti posso aiutare</p>
            <h2 className="mt-3 font-display italic text-5xl md:text-6xl text-[#3D2140]">Consulenze <span className="text-[#FF2768]">& Presenza</span></h2>
            <p className="mt-4 text-[#3D2140]/70 max-w-2xl mx-auto">Un percorso pensato su misura, dalla prima chiacchierata alla presenza a casa tua. <em className="text-[#4E9EA5]">Scrivimi per avere info.</em></p>
          </div>

          {/* TESTIMONIALS ROW */}
          {testimonials.length > 0 && (
            <div className="mb-14 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="testimonials-row">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="bg-white/70 backdrop-blur rounded-2xl p-5 border border-[#FF2768]/15" data-testid={`testimonial-${t.id}`}>
                  <Quote size={16} className="text-[#FF2768] mb-2" />
                  <p className="text-sm text-[#3D2140]/85 italic leading-relaxed">"{t.quote}"</p>
                  <p className="mt-3 text-xs font-bold text-[#FF2768]">— {t.name}{t.role && <span className="text-[#3D2140]/60 font-normal">, {t.role}</span>}</p>
                </div>
              ))}
            </div>
          )}

          {/* CONSULENZE carousel */}
          <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4E9EA5] font-bold">Sezione 1</p>
              <h3 className="mt-1 font-display text-2xl text-[#3D2140]">Consulenze Online</h3>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scroll(consRef, -1)} className="w-10 h-10 rounded-full bg-white shadow-md hover:bg-[#FF2768] hover:text-white flex items-center justify-center" data-testid="cons-scroll-left" aria-label="Scorri a sinistra"><ChevronLeft size={18} /></button>
              <button onClick={() => scroll(consRef, 1)} className="w-10 h-10 rounded-full bg-white shadow-md hover:bg-[#FF2768] hover:text-white flex items-center justify-center" data-testid="cons-scroll-right" aria-label="Scorri a destra"><ChevronRight size={18} /></button>
            </div>
          </div>

          <div ref={consRef} className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            {CONSULENZE.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="min-w-[300px] md:min-w-[320px] snap-start bg-white rounded-3xl p-6 shadow-sm border border-[#3D2140]/8 hover:shadow-xl transition-shadow flex flex-col" data-testid={`cons-card-${s.key}`}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: s.accent + "35" }}>
                    <Icon size={18} style={{ color: s.accent }} />
                  </div>
                  <h4 className="font-display text-2xl text-[#3D2140]">{s.label}</h4>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full" style={{ background: s.accent + "20", color: s.accent }}>{s.mode}</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#3D2140]/60">{s.duration}</span>
                    {s.note && <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF2768]">· {s.note}</span>}
                  </div>
                  <p className="mt-4 text-sm text-[#3D2140]/70 leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-5 flex flex-col gap-2">
                    <button onClick={() => { trackClick("richiedi-info", s.key); setModal({ key: s.key, label: s.label }); }} className="w-full py-2.5 rounded-full bg-[#FF2768] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#e91e5e]" data-testid={`cons-info-${s.key}`}>
                      🌸 Richiedi info
                    </button>
                    <a href={CALENDLY_URL} onClick={() => trackClick("call-conoscitiva", s.key)} target="_blank" rel="noopener noreferrer" className="w-full text-center py-2.5 rounded-full bg-white border-2 border-[#4E9EA5] text-[#4E9EA5] text-xs font-bold uppercase tracking-widest hover:bg-[#4E9EA5] hover:text-white" data-testid={`cons-call-${s.key}`}>
                      📞 Call conoscitiva
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[10px] text-[#3D2140]/50 italic">Tutte le consulenze si svolgono in videochiamata (Zoom, Google Meet o WhatsApp).</p>

          {/* TATA A DOMICILIO carousel */}
          <div className="mt-16 mb-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#4E9EA5] font-bold">Sezione 2</p>
              <h3 className="mt-1 font-display text-2xl text-[#3D2140]">Tata a Domicilio*</h3>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scroll(tataRef, -1)} className="w-10 h-10 rounded-full bg-white shadow-md hover:bg-[#FF2768] hover:text-white flex items-center justify-center" data-testid="tata-scroll-left" aria-label="Scorri a sinistra"><ChevronLeft size={18} /></button>
              <button onClick={() => scroll(tataRef, 1)} className="w-10 h-10 rounded-full bg-white shadow-md hover:bg-[#FF2768] hover:text-white flex items-center justify-center" data-testid="tata-scroll-right" aria-label="Scorri a destra"><ChevronRight size={18} /></button>
            </div>
          </div>

          <div ref={tataRef} className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            {TATA_DOMICILIO.map((s) => (
              <div key={s.key} className="min-w-[300px] md:min-w-[340px] snap-start bg-white rounded-3xl p-6 shadow-sm border border-[#3D2140]/8 hover:shadow-xl transition-shadow flex flex-col" data-testid={`tata-card-${s.key}`}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: s.accent + "35" }}>
                  <Home size={18} style={{ color: s.accent }} />
                </div>
                <h4 className="font-display text-2xl text-[#3D2140]">{s.label}</h4>
                <div className="mt-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ background: s.accent + "20", color: s.accent }}><MapPin size={10} /> Bologna e limitrofi</span>
                </div>
                <p className="mt-4 text-sm text-[#3D2140]/70 leading-relaxed flex-1">{s.desc}</p>
                <div className="mt-5 flex flex-col gap-2">
                  <button onClick={() => { trackClick("richiedi-info", s.key); setModal({ key: s.key, label: s.label }); }} className="w-full py-2.5 rounded-full bg-[#FF2768] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#e91e5e]" data-testid={`tata-info-${s.key}`}>
                    🌸 Richiedi info
                  </button>
                  <a href={CALENDLY_URL} onClick={() => trackClick("call-conoscitiva", s.key)} target="_blank" rel="noopener noreferrer" className="w-full text-center py-2.5 rounded-full bg-white border-2 border-[#4E9EA5] text-[#4E9EA5] text-xs font-bold uppercase tracking-widest hover:bg-[#4E9EA5] hover:text-white" data-testid={`tata-call-${s.key}`}>
                    📞 Call conoscitiva
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-[#3D2140]/50 italic">*Bologna e comuni limitrofi</p>
        </div>
      </section>

      {modal && <InquiryModal service={modal} onClose={() => setModal(null)} />}
    </>
  );
}

function InquiryModal({ service, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.consent) { toast.error("Compila i campi obbligatori"); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/service-inquiry`, {
        service_key: service.key,
        service_label: service.label,
        ...form,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="inquiry-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Richiesta informazioni</p>
            <h3 className="font-display italic text-2xl text-[#3D2140] mt-1">{service.label}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#FAF8F5]" data-testid="inquiry-close"><X size={18} /></button>
        </div>

        {sent ? (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-full bg-[#FF2768] text-white flex items-center justify-center mx-auto mb-4">💗</div>
            <h4 className="font-display italic text-2xl text-[#3D2140]">Grazie, ti risponderò io</h4>
            <p className="mt-3 text-sm text-[#3D2140]/70">Ho ricevuto la tua richiesta. Ti scriverò a <strong>{form.email}</strong> entro 24 ore con tutte le info sui costi e come procedere.</p>
            <button onClick={onClose} className="mt-6 px-5 py-2.5 rounded-full bg-[#3D2140] text-white text-sm font-semibold" data-testid="inquiry-close-success">Chiudi</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome *" className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="inquiry-name" />
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="inquiry-email" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono / WhatsApp" className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="inquiry-phone" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Cosa vorresti sapere?" className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none resize-none" data-testid="inquiry-message" />
            <label className="flex items-start gap-2 text-xs text-[#3D2140]/70 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#FF2768]" required data-testid="inquiry-consent" />
              <span>Confermo di aver letto la privacy policy e acconsento al trattamento dei dati.</span>
            </label>
            <button type="submit" disabled={sending} className="w-full py-3 rounded-full bg-[#FF2768] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e91e5e] disabled:opacity-40" data-testid="inquiry-submit">
              {sending ? "Sto inviando..." : "🌸 Invia richiesta"}
            </button>
            <div className="flex gap-2 mt-3 items-center justify-center text-[10px] text-[#3D2140]/50">
              <Phone size={11} /> Preferisci una chat veloce? <a href="https://wa.me/393403993522" target="_blank" rel="noopener noreferrer" className="text-[#FF2768] font-semibold underline">WhatsApp</a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
